// Stage Mail-V2 (2026-07-22 / Step 20): Account List Dialog — 列出 / 启用-停用 / 削除。
//
// 设计:
//   - 显示 所有 accounts,real mode 走 mailV2Api.listAccounts
//   - 每行: enable/disable toggle + 削除 按钮 + test-imap / test-smtp 按钮
//   - 削除 调 mailV2Api.deleteAccount + onChanged
//   - 失败 → toast + 不关闭 dialog

import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { mailV2Api, type MailAccountV2DTO } from "../../../api/mail-v2";

interface MailV2AccountListDialogProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  onError: (msg: string) => void;
  onInfo: (msg: string) => void;
  accounts?: MailAccountV2DTO[];
}

export function MailV2AccountListDialog({
  open,
  onClose,
  onChanged,
  onError,
  onInfo,
  accounts,
}: MailV2AccountListDialogProps) {
  const [localAccounts, setLocalAccounts] = useState<MailAccountV2DTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (accounts !== undefined) {
      setLocalAccounts(accounts);
      return;
    }
    let cancelled = false;
    setLoading(true);
    mailV2Api
      .listAccounts()
      .then((rows) => {
        if (cancelled) return;
        setLocalAccounts(rows);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        onError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, accounts, onError]);

  if (!open) return null;

  const handleToggleActive = async (acc: MailAccountV2DTO) => {
    setBusyId(acc.id);
    try {
      const updated = await mailV2Api.updateAccount(acc.id, {
        is_active: !acc.is_active,
      });
      setLocalAccounts((rows) => rows.map((r) => (r.id === acc.id ? updated : r)));
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (acc: MailAccountV2DTO) => {
    if (!window.confirm(`アカウント「${acc.email_address}」を削除しますか?`)) return;
    setBusyId(acc.id);
    try {
      await mailV2Api.deleteAccount(acc.id);
      setLocalAccounts((rows) => rows.filter((r) => r.id !== acc.id));
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleTestImap = async (acc: MailAccountV2DTO) => {
    setBusyId(acc.id);
    try {
      const r = await mailV2Api.testImap(acc.id);
      if (r.status === "ok") onInfo(`IMAP 接続成功: ${acc.email_address}`);
      else onError(`IMAP 失敗: ${r.error || "unknown"}`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleTestSmtp = async (acc: MailAccountV2DTO) => {
    setBusyId(acc.id);
    try {
      const r = await mailV2Api.testSmtp(acc.id);
      if (r.status === "ok") onInfo(`SMTP 接続成功: ${acc.email_address}`);
      else onError(`SMTP 失敗: ${r.error || "unknown"}`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mv2-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="アカウント管理">
      <div className="mv2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mv2-modal-header">
          <h2>アカウント管理</h2>
          <button type="button" className="mv2-modal-close" onClick={onClose} aria-label="閉じる">×</button>
        </div>
        <div className="mv2-modal-body">
          {loading && <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>読み込み中...</div>}
          {!loading && localAccounts.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
              アカウントがありません。「メールアカウントを追加」から作成してください。
            </div>
          )}
          {!loading && localAccounts.map((acc) => (
            <div key={acc.id} className="mv2-account-row">
              <div className="mv2-account-row-info">
                <div className="mv2-account-row-email">{acc.email_address}</div>
                <div className="mv2-account-row-meta">
                  {acc.name} · {acc.account_type} · {acc.imap_host}:{acc.imap_port}
                </div>
              </div>
              <div className="mv2-account-row-actions">
                <Button
                  variant="ghost"
                  onClick={() => handleToggleActive(acc)}
                  disabled={busyId === acc.id}
                  aria-label={acc.is_active ? "無効化" : "有効化"}
                >
                  {acc.is_active ? "有効" : "無効"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => handleTestImap(acc)}
                  disabled={busyId === acc.id}
                  aria-label="IMAP テスト"
                >
                  IMAP
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => handleTestSmtp(acc)}
                  disabled={busyId === acc.id}
                  aria-label="SMTP テスト"
                >
                  SMTP
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleDelete(acc)}
                  disabled={busyId === acc.id}
                  aria-label="削除"
                >
                  削除
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mv2-modal-footer">
          <Button variant="primary" onClick={onClose}>閉じる</Button>
        </div>
      </div>
    </div>
  );
}